import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import { UserStore, useUserStore } from './stores/UserStore';
import { User } from './types';

type UserContextValue = {
  data: null | User;
  error?: null | Error;
  loading: boolean;
  enabled: boolean;
  retry(): void;
  setUser(data: UserContextValue['data']): void;
  setEnabled?: (enabled: boolean) => void;
};

type UserProviderProps = {
  redirectLoggedOutUsers?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

/**
 * Context for current user.
 */
const UserContext = getOrCreateGlobalContext<UserContextValue>(
  CONTEXT_NAMES.UserContext,
);

const useUserStoreWithValidation = () =>
  useStoreWithValidation<UserStore>(CONTEXT_NAMES.UserContext, {
    hook: 'useUser',
    provider: 'UserProvider',
  });

/**
 * Returns the current user without redirecting on 401. Returns `null` for `data` when the user is unauthenticated (401 response), instead of throwing or redirecting.
 */
export const useOptionalUser = () => {
  const { error, loading, data, setData, fetchData, retry } =
    useUserStoreWithValidation();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    error,
    loading,
    data: error?.status === 401 ? null : data,
    retry,
    setUser: setData,
    setEnabled: noop,
  };
};

export const useUserProvider = (
  redirectLoggedOutUsers: boolean,
  disabled?: boolean,
) => {
  const { data, error, loading, setData, retry, fetchData } = useUserStore(
    redirectLoggedOutUsers,
  );

  useEffect(() => {
    if (!disabled) {
      fetchData();
    }
  }, [disabled, fetchData]);

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.UserContext);
    fetchData();
  }, [fetchData]);

  return useMemo(
    () => ({
      error,
      loading,
      data: error?.status === 401 ? null : data,
      enabled: !disabled,
      retry,
      setUser: setData,
      setEnabled: legacySetEnabled,
    }),
    [error, loading, data, disabled, retry, setData, legacySetEnabled],
  );
};

export const UserProvider: ComponentType<UserProviderProps> = ({
  children,
  redirectLoggedOutUsers = true,
  disabled,
}) => {
  const value = useUserProvider(redirectLoggedOutUsers, disabled);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.displayName = 'UserProviderComponent';
